import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"

import { TrialBlock } from "@/types/trialBlock"

import { FormInput, FormValues, defaultFormValues, formSchema } from "./ReviewFormSchema"

export const useReviewForm = (onSubmit: (data: FormValues) => void, trialBlock?: TrialBlock) => {
  // Map trial block data to form values if available
  // Ensure trialShiftResult is either "Passed" or "Failed"
  const ensureValidResult = (result: string | undefined): "Passed" | "Failed" => {
    return result === "Passed" ? "Passed" : "Failed"
  }

  const initialValues = trialBlock
    ? {
        trialShiftResult: ensureValidResult(trialBlock.result),
        requireAnotherTrial: false, // This will be set based on DB data when we have it
        attended: trialBlock.attended !== undefined ? trialBlock.attended : false,
        deliveryProtocolScores: {
          deliveryPictures: trialBlock.deliveryProtocolScores?.deliveryPictures || "",
          deliveryNotes: trialBlock.deliveryProtocolScores?.deliveryNotes || "",
          instructionCompliance: trialBlock.deliveryProtocolScores?.instructionCompliance || "",
        },
        deliverySuccessRate: trialBlock.deliveryProtocolScores?.deliverySuccess || "",
        roadSafetyScores: {
          drivingBehaviour: trialBlock.roadSafetyScores?.drivingBehaviour || "",
          vehicleHandling: trialBlock.roadSafetyScores?.vehicleHandling || "",
        },
        safetyScore: trialBlock.roadSafetyScores?.safety ?? undefined,
        customerInteractionScores: {
          customerFeedback: trialBlock.customerInteractionScores?.customerFeedback || "",
          issueResolution: trialBlock.customerInteractionScores?.issueResolution || "",
        },
        // Map additional fields from the database
        timeManagement: trialBlock.timeManagement || "",
        loadCheck: trialBlock.loadCheck || "",
        comments: trialBlock.comments || "",
      }
    : defaultFormValues

  const form = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
  })

  const calculateResult = () => {
    // Count the number of ratings that are "Failed Protocol" or have significant issues
    let failedProtocol = 0
    let needsImprovement = 0

    // Check delivery protocol scores
    Object.values(form.getValues("deliveryProtocolScores")).forEach((value) => {
      if (value === "Failed Protocol") failedProtocol++
      if (value === "Needs Improvement") needsImprovement++
    })

    // Check delivery success rate
    const deliverySuccess = form.getValues("deliverySuccessRate")
    if (deliverySuccess === "Failed Protocol") failedProtocol++
    if (deliverySuccess === "Needs Improvement") needsImprovement++

    // Check road safety scores
    Object.values(form.getValues("roadSafetyScores")).forEach((value) => {
      if (value === "Failed Protocol") failedProtocol++
      if (value === "Needs Improvement") needsImprovement++
    })

    // Check customer interaction scores
    Object.values(form.getValues("customerInteractionScores")).forEach((value) => {
      if (value === "Failed Protocol") failedProtocol++
      if (value === "Needs Improvement") needsImprovement++
    })

    // Check time management
    const timeManagement = form.getValues("timeManagement")
    if (timeManagement === "Behind") failedProtocol++

    // Check load check
    const loadCheck = form.getValues("loadCheck")
    if (loadCheck === "Not Loaded") failedProtocol++

    // If there are any "Failed Protocol" ratings or more than 3 "Needs Improvement", it's a fail
    if (failedProtocol > 0 || needsImprovement > 3) {
      form.setValue("trialShiftResult", "Failed")
    } else {
      form.setValue("trialShiftResult", "Passed")
    }
  }

  const handleFormSubmit = (data: FormValues) => {
    onSubmit(data)
  }

  return {
    form,
    calculateResult,
    handleFormSubmit,
  }
}

export default useReviewForm
